import { useEffect, useMemo, useState } from "react";
import { FormRenderer } from "@sga/shared/form-renderer";
import { fetchSubmissionForEdit, saveSubmissionEdit, type EditPayload } from "./api.js";

/**
 * Changing answers that have already been submitted.
 *
 * Shared by both places an applicant can reach this from: the form link they were given, and the
 * portal's application list. Same screen, same rules, one implementation — the alternative is two
 * edit screens that disagree about what may be changed.
 *
 * Every page of the form at once, rather than the paged walk `FormFlow` uses. Someone amending is
 * looking for one answer they got wrong, and making them click Next through four pages to find it
 * is the opposite of helpful.
 *
 * Sensitive answers come back blank — they are encrypted at rest and reading one back is the
 * portal's audited reveal, not a side effect of opening this screen. Left blank, they are left
 * alone.
 */
export const EditFlow = ({
  submissionId,
  colors,
  branding,
  lookups,
  onDone,
  onCancel,
}: {
  submissionId: string;
  colors: Record<string, string>;
  branding: Record<string, string>;
  /** The same shape `App.tsx` already hands to FormFlow and PreviewFlow. */
  lookups: {
    states: { code: string; name: string }[];
    lgasForState: (stateName: string) => { id: string; name: string }[];
    onStateSelected: (state: string) => void;
  };
  onDone: (changedKeys: string[]) => void;
  onCancel: () => void;
}): JSX.Element => {
  const [payload, setPayload] = useState<EditPayload | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    void fetchSubmissionForEdit(submissionId)
      .then((result) => {
        if (!live) return;
        setPayload(result);
        setValues(result.answers);
        // A state already answered needs its LGA list before the field can show the saved value.
        for (const field of result.schema.fields) {
          const value = result.answers[field.key];
          if (field.type === "ng_state_lga") {
            const state = (value as { state?: string })?.state;
            if (state) lookups.onStateSelected(state);
          } else if (
            field.stateLgaPairing?.role === "state" &&
            typeof value === "string" &&
            value
          ) {
            lookups.onStateSelected(value);
          }
        }
      })
      .catch((caught: unknown) => {
        if (live) {
          setLoadError(
            caught instanceof Error ? caught.message : "Could not open your application.",
          );
        }
      });
    return () => {
      live = false;
    };
  }, [submissionId, lookups]);

  const rendererBranding = useMemo(() => ({ ...colors, ...branding }), [colors, branding]);

  const save = async (): Promise<void> => {
    if (!payload) return;
    setSaving(true);
    setSaveError(null);
    setErrors({});
    try {
      const result = await saveSubmissionEdit(submissionId, values);
      onDone(result.changedKeys);
    } catch (caught: unknown) {
      const details = (caught as { details?: { issues?: { key: string; message: string }[] } })
        .details;
      if (details?.issues?.length) {
        // The server validates with the same validator the form uses, so its complaints land on
        // the same fields — shown in place rather than as one message at the bottom.
        setErrors(Object.fromEntries(details.issues.map((issue) => [issue.key, issue.message])));
        setSaveError("Some answers need correcting before this can be saved.");
      } else {
        setSaveError(
          caught instanceof Error ? caught.message : "Could not save your changes. Please retry.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div
        className="rounded-lg border p-5 text-sm"
        role="alert"
        style={{ borderColor: colors.border, background: colors.surface }}
      >
        {loadError}
      </div>
    );
  }

  if (!payload) {
    return (
      <p className="p-5 text-sm" role="status" style={{ color: colors.text }}>
        Opening your application…
      </p>
    );
  }

  if (!payload.submission.canEdit) {
    return (
      <div
        className="rounded-lg border p-5"
        style={{ borderColor: colors.border, background: colors.surface }}
      >
        <p className="text-sm font-medium">Your answers can no longer be changed</p>
        <p className="mt-1 text-sm opacity-80">
          {payload.submission.reason ?? "This application is now closed for changes."}
        </p>
        {payload.submission.reference && (
          <p className="mt-3 text-sm">
            Your reference is <strong className="font-mono">{payload.submission.reference}</strong>.
            Quote it if you need to contact the programme team.
          </p>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="mt-4 rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: colors.border }}
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className="rounded-lg border p-4 text-sm"
        style={{ borderColor: colors.border, background: colors.surface }}
      >
        <p className="font-medium">Changing your answers</p>
        <p className="mt-1 opacity-80">
          {payload.submission.reference ? (
            <>
              Reference <strong className="font-mono">{payload.submission.reference}</strong> — this
              stays the same.{" "}
            </>
          ) : null}
          Your changes replace what you submitted. You can keep changing them while the form is
          open.
        </p>
      </div>

      <div aria-live="polite" role="status" className="sr-only">
        {saveError ?? ""}
      </div>

      <FormRenderer
        fields={payload.schema.fields}
        sections={payload.schema.sections}
        logic={payload.schema.logic as never}
        values={values}
        errors={errors}
        onChange={(key, value) => {
          setValues((current) => ({ ...current, [key]: value }));
          const field = payload.schema.fields.find((entry) => entry.key === key);
          if (field?.type === "ng_state_lga") {
            const state = (value as { state?: string })?.state;
            if (state) lookups.onStateSelected(state);
          } else if (field?.stateLgaPairing?.role === "state" && typeof value === "string") {
            if (value) lookups.onStateSelected(value);
          }
        }}
        branding={rendererBranding as never}
        lookups={{ states: lookups.states, lgasForState: lookups.lgasForState }}
      />

      {saveError && (
        <p
          role="alert"
          className="rounded-md p-3 text-sm"
          style={{ background: `${colors.error}15`, color: colors.error }}
        >
          {saveError}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className="rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-60"
          style={{ background: colors.primary, color: colors.primaryContrast }}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border px-4 py-2.5 text-sm"
          style={{ borderColor: colors.border }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
